import { useAuth } from '../context/AuthContext';

/**
 * Hook para verificar permisos del usuario actual
 * 
 * Uso basico:
 * const { canView, canCreate, canEdit, canDelete, isReadOnly } = usePermissions('registros');
 * 
 * Uso operativo:
 * const { canService, canAction, canInventoryAction } = usePermissions('registros');
 * canService('id-servicio-costura') // true/false
 * canAction('crear_movimientos')    // true/false
 * canInventoryAction('dar_salida_mp') // true/false
 */
export const usePermissions = (tabla) => {
  const { user, isAdmin } = useAuth();

  // Admin tiene todos los permisos
  if (isAdmin()) {
    return {
      canView: true,
      canCreate: true,
      canEdit: true,
      canDelete: true,
      isReadOnly: false,
      isAdmin: true,
      canService: () => true,
      canAction: () => true,
      canInventoryAction: () => true,
      canChangeToState: () => true,
      serviciosPermitidos: [],
      estadosPermitidos: [],
      todosServicios: true,
    };
  }

  // Usuario de solo lectura
  if (user?.rol === 'lectura') {
    return {
      canView: true,
      canCreate: false,
      canEdit: false,
      canDelete: false,
      isReadOnly: true,
      isAdmin: false,
      canService: () => false,
      canAction: () => false,
      canInventoryAction: () => false,
      canChangeToState: () => false,
      serviciosPermitidos: [],
      estadosPermitidos: [],
      todosServicios: false,
    };
  }

  // Usuario normal - verificar permisos especificos
  const permisos = user?.permisos || {};
  const permisosTabla = permisos[tabla] || {};
  const operativos = permisos._operativos || {};
  const serviciosPermitidos = operativos.servicios_permitidos || [];
  const todosServicios = serviciosPermitidos.length === 0; // vacío = todos permitidos
  const accionesProduccion = operativos.acciones_produccion || {};
  const accionesInventario = operativos.acciones_inventario || {};
  const estadosPermitidos = operativos.estados_permitidos || []; // vacío = todos

  // Check if user can operate on a specific service
  const canService = (servicioId) => {
    if (todosServicios) return true;
    return serviciosPermitidos.includes(servicioId);
  };

  // Check production action
  const canAction = (actionKey) => {
    // Si no hay operativos definidos, permitir todo (backward compatibility)
    if (!operativos.acciones_produccion) return true;
    return accionesProduccion[actionKey] === true;
  };

  // Check inventory action
  const canInventoryAction = (actionKey) => {
    if (!operativos.acciones_inventario) return true;
    return accionesInventario[actionKey] === true;
  };

  // Check if user can change to a specific state.
  // Normaliza comparando sin tildes y en minúsculas, porque los estados de la
  // ruta de producción pueden llevar acentos ("Para Lavandería") mientras que
  // el catálogo histórico de permisos los guardó sin acentos ("Para Lavanderia").
  const normalizeEstado = (s) => (s || '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')  // quita diacríticos
    .trim()
    .toLowerCase();

  const canChangeToState = (estado) => {
    if (estadosPermitidos.length === 0) return true; // vacío = todos
    const target = normalizeEstado(estado);
    return estadosPermitidos.some(e => normalizeEstado(e) === target);
  };

  return {
    canView: permisosTabla.ver === true,
    canCreate: permisosTabla.crear === true,
    canEdit: permisosTabla.editar === true,
    canDelete: permisosTabla.eliminar === true,
    isReadOnly: !permisosTabla.crear && !permisosTabla.editar && !permisosTabla.eliminar,
    isAdmin: false,
    canService,
    canAction,
    canInventoryAction,
    canChangeToState,
    serviciosPermitidos,
    estadosPermitidos,
    todosServicios,
  };
};

/**
 * Mapeo de rutas a tablas de permisos
 */
export const RUTA_A_TABLA = {
  '/': 'dashboard',
  '/productos-odoo': 'productos_odoo',
  '/marcas': 'marcas',
  '/generos': 'generos',
  '/tipos': 'tipos',
  '/entalles': 'entalles',
  '/telas': 'telas',
  '/telas-general': 'telas_general',
  '/hilos': 'hilos',
  '/hilos-especificos': 'hilos_especificos',
  '/tallas-catalogo': 'tallas',
  '/colores-catalogo': 'colores',
  '/colores-generales': 'colores_generales',
  '/colores-por-tipo': 'colores_por_tipo',
  '/cuellos': 'cuellos',
  '/detalles': 'detalles',
  '/lavados': 'lavados',
  '/bases': 'bases',
  '/modelos': 'modelos',
  '/registros': 'registros',
  '/mesa-acabado': 'reporte_calidad',
  '/inventario': 'inventario',
  '/inventario/ingresos': 'inventario_ingresos',
  '/inventario/salidas': 'inventario_salidas',
  '/inventario/salidas-libres': 'inventario_salidas_libres',
  '/inventario/ajustes': 'inventario_ajustes',
  '/inventario/rollos': 'inventario_rollos',
  '/inventario/movimientos': 'reporte_movimientos',
  '/inventario/kardex': 'kardex',
  '/inventario/kardex-pt': 'kardex_pt',
  '/inventario/kardex-general': 'kardex_general',
  '/inventario/alertas-stock': 'reporte_stock_bajo',
  '/inventario/transferencias-linea': 'transferencias_linea',
  '/maestros/servicios': 'servicios_produccion',
  '/maestros/personas': 'personas_produccion',
  '/maestros/rutas': 'rutas_produccion',
  '/maestros/movimientos': 'movimientos_produccion',
  '/maestros/motivos-incidencia': 'motivos_incidencia',
  '/maestros/productividad': 'reporte_productividad',
  '/guias': 'guias_remision',
  '/reportes/cortes': 'registros',
  '/reportes/conciliacion-pendiente': 'registros',
  '/reportes/validacion-registros': 'reporte_validacion_registros',
  '/reportes/seguimiento': 'reporte_seguimiento',
  '/reportes/entregas': 'reporte_entregas',
  '/reportes/costo-lote': 'reporte_costo_lote',
  '/reportes/operativo': 'reporte_operativo',
  '/reportes/balance-terceros': 'reporte_operativo',
  '/reportes/costura': 'reporte_operativo',
  '/reportes/tiempos-muertos': 'reporte_operativo',
  '/reportes/calidad': 'reporte_calidad',
  '/reportes/valorizacion': 'reporte_valorizacion',
  '/reportes/lotes': 'reporte_lotes',
  '/reportes/matriz': 'reporte_matriz',
  '/reportes/muestras-lavanderia': 'reporte_muestras_lavanderia',
  '/reportes/rendimiento-servicios': 'reporte_rendimiento_servicios',
  '/reportes/despachos-tienda': 'reporte_despachos_tienda',
  '/reportes/seguimiento-tienda': 'reporte_despachos_tienda',
  '/reportes/movimientos-costos': 'reporte_movimientos_costos',
  '/reportes/trazabilidad': 'reporte_lotes',
  '/reportes/alertas': 'reporte_seguimiento',
  '/reportes/agenda-entregas': 'reporte_entregas',
  '/reportes/tendencia-fallados': 'reporte_calidad',
  '/reportes/mermas': 'reporte_calidad',
  '/reportes/lotes-fraccionados': 'reporte_lotes',
  '/reportes/paralizados': 'reporte_seguimiento',
  '/reportes/trazabilidad-general': 'reporte_lotes',
  '/reportes/mp-valorizado': 'reporte_valorizacion',
  '/reportes/wip': 'reporte_valorizacion',
  '/reportes/pt-valorizado': 'reporte_valorizacion',
  '/calidad/merma': 'reporte_calidad',
  '/calidad/reporte-mermas': 'reporte_calidad',
  '/control-fallados': 'reporte_calidad',
  '/config-empresa': 'config_empresa',
  '/usuarios': 'usuarios',
  '/historial-actividad': 'historial_actividad',
  '/auditoria': 'auditoria',
  '/backups': 'backups',
};

export default usePermissions;
